import * as THREE from 'three/webgpu';

import { configureSpriteTexture } from '../assets/playerSprite.js';
import { buildRig } from './creatures.js';
import { defineEnemyType } from './enemyTypes.js';

/**
 * @file Loads an {@link EnemyType} from AUTHORED art — the replacement for the
 * procedural creature generators, which were scrapped because randomised
 * primitive assembly cannot produce an intentional silhouette.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ART KINDS ARE A REGISTRY, NOT A SWITCH
 * ─────────────────────────────────────────────────────────────────────────────
 * A definition's `art` block names one entry per KIND (`sprite`, `rig`, …).
 * Each kind is a loader registered in {@link registerEnemyArtKind} that returns
 * `{ sprite?, rig? }` backend payloads. Adding another kind therefore touches
 * this file only: the renderers, `Gameplay` and every level keep reading
 * `type.sprite` / `type.rig`. (The `model` kind — external `.glb` loading — was
 * DELETED, owner decision 2026-08-03: creatures are built in code. See
 * `engine/actors/creatures.js` and HANDOFF-RIG.md.)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CREATURE IS 2D **OR** 3D, NEVER BOTH
 * ─────────────────────────────────────────────────────────────────────────────
 * Owner decision, and it is why there is no runtime A/B toggle any more. A def
 * that names both kinds keeps the RIG and frees the sprite here, so exactly
 * one backend payload ever reaches the type. `Gameplay` routes each type to the
 * matching renderer once, at construction (per-type masks), and a level is free
 * to mix 2D creatures with 3D ones.
 *
 * `art.worldHeight` is still authoritative for every kind — a rig's assembled
 * rest pose is SCALED to it, a sprite frame is DRAWN at it — so re-authoring a
 * creature from a PNG to a rig does not resize it in the world.
 *
 * ⚠ The two backends light differently and this is not a bug: sprites are unlit
 * and take the raw probe tint (they ARE painted art), rig parts are baked and
 * take a NORMALISED probe tint. Never brighten a creature's albedo without
 * checking which path you are looking at.
 *
 * ⚠ Sprites hover via `anchorY`, never via a Y offset. `SpriteBatch` puts the
 * world position at `anchorY` up from the frame bottom, so a NEGATIVE anchor
 * lifts the whole quad clear of the ground — which is how a bat or a floating
 * eye stops standing on its own shadow.
 *
 * @module engine/actors/enemyArt
 */

/** @typedef {import('../types.js').EnemyType} EnemyType */
/** @typedef {import('../types.js').EnemyDef} EnemyDef */
/** @typedef {import('../types.js').EnemyArtResult} EnemyArtResult */

/** Bump when a field changes meaning; loaders migrate older defs. */
export const ENEMY_DEF_VERSION = 2;

/** Fallback height when a def names none, metres. */
const DEFAULT_WORLD_HEIGHT = 1.4;

/** @type {Map<string, (art: any, ctx: any) => Promise<EnemyArtResult>>} */
const ART_LOADERS = new Map();

/** Warn once per message — a broken def is spammed every spawn otherwise. */
const _warned = new Set();
/** @param {string} msg */
function warnOnce(msg) {
  if (_warned.has(msg)) return;
  _warned.add(msg);
  console.warn(`[enemyArt] ${msg}`);
}

/**
 * Registers an art kind. The name is the key inside a def's `art` block.
 *
 * @param {string} kind e.g. `'sprite'`, `'rig'`.
 * @param {(art: any, ctx: {def: EnemyDef, worldHeight: number, renderer: any,
 *   caps: any, sharedMaterial: any}) => Promise<EnemyArtResult>} loader Resolves
 *   to the backend payloads it produced, plus an optional `dispose` for anything
 *   `EnemyType.dispose` does not already free (render targets, own materials).
 */
export function registerEnemyArtKind(kind, loader) {
  if (!kind || typeof loader !== 'function') throw new Error('registerEnemyArtKind: need (kind, loader)');
  ART_LOADERS.set(kind, loader);
}

/** @returns {string[]} Registered kind names, in registration order. */
export function enemyArtKinds() {
  return [...ART_LOADERS.keys()];
}

// ─────────────────────────────────────────────────────────────────────────────
// Definition normalisation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validates + normalises an authored `*.enemy.json`. Pure: no GPU, no fetch, so
 * the unit tests can pin the contract headlessly.
 *
 * @param {EnemyDef} def
 * @returns {EnemyDef} A normalised COPY: every art kind carries the resolved
 *   `worldHeight`, and `stats`/`spawn` always exist.
 */
export function normalizeEnemyDef(def) {
  if (!def || typeof def !== 'object') throw new Error('enemy def: not an object');
  const id = def.id;
  if (typeof id !== 'string' || !id) throw new Error('enemy def: `id` is required');
  if (def.version !== ENEMY_DEF_VERSION) {
    warnOnce(`${id}: version ${def.version} != ${ENEMY_DEF_VERSION} (loading anyway)`);
  }

  const art = def.art;
  if (!art || typeof art !== 'object') throw new Error(`enemy def ${id}: an \`art\` block is required`);

  const kinds = Object.keys(art).filter((k) => k !== 'worldHeight' && art[k] && typeof art[k] === 'object');
  if (kinds.length === 0) throw new Error(`enemy def ${id}: \`art\` names no kind (sprite / model)`);

  // One height for every backend — see the header.
  let worldHeight = num(art.worldHeight);
  if (!(worldHeight > 0)) {
    for (const k of kinds) {
      const h = num(art[k].worldHeight);
      if (h > 0) {
        worldHeight = h;
        break;
      }
    }
  }
  if (!(worldHeight > 0)) worldHeight = DEFAULT_WORLD_HEIGHT;

  /** @type {Record<string, any>} */
  const outArt = { worldHeight };
  for (const k of kinds) {
    const src = art[k];
    if (num(src.worldHeight) > 0 && Math.abs(num(src.worldHeight) - worldHeight) > 1e-6) {
      warnOnce(`${id}: art.${k}.worldHeight disagrees with art.worldHeight — the shared value wins`);
    }
    // NO url check here: whether a kind needs a file is the LOADER's business
    // (`loadSpriteArt` requires one; the `rig` kind names a generator instead).
    // Keeping normalisation kind-agnostic is what lets a file-less art kind
    // exist at all — see HANDOFF-RIG §3.1.
    outArt[k] = { ...src, worldHeight };
  }

  return {
    version: ENEMY_DEF_VERSION,
    id,
    name: def.name ?? id,
    art: outArt,
    stats: { ...(def.stats ?? {}) },
    spawn: { ...(def.spawn ?? {}) },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The loader
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds one {@link EnemyType} from an authored definition.
 *
 * Every art kind in the def loads in PARALLEL and failures are per-kind: a
 * broken rig generator still leaves you the sprite. Only when nothing loads does this
 * reject — so a caller can skip the type rather than lose the level.
 *
 * @param {EnemyDef} def Raw or normalised; normalised here either way.
 * @param {Object} [opts]
 * @param {any} [opts.renderer] Live renderer, for kinds that need a GPU.
 * @param {import('../types.js').EngineCaps|null} [opts.caps]
 * @param {THREE.Material|null} [opts.sharedMaterial] Reused by mesh backends
 *   instead of minting one material per type at load.
 * @param {number} [opts.anisotropy=1]
 * @returns {Promise<EnemyType>}
 */
export async function loadEnemyType(def, opts = {}) {
  const d = normalizeEnemyDef(def);
  const worldHeight = d.art.worldHeight;
  const ctx = {
    def: d,
    worldHeight,
    renderer: opts.renderer ?? null,
    caps: opts.caps ?? null,
    sharedMaterial: opts.sharedMaterial ?? null,
    anisotropy: opts.anisotropy ?? 1,
  };

  const kinds = Object.keys(d.art).filter((k) => k !== 'worldHeight');
  const settled = await Promise.all(
    kinds.map(async (kind) => {
      const load = ART_LOADERS.get(kind);
      if (!load) {
        warnOnce(`${d.id}: unknown art kind "${kind}" — is it registered?`);
        return null;
      }
      try {
        return await load(d.art[kind], ctx);
      } catch (err) {
        console.warn(`[enemyArt] ${d.id}: ${kind} failed to load (${err?.message ?? err})`);
        return null;
      }
    }),
  );

  /** @type {EnemyArtResult[]} */
  let parts = settled.filter(Boolean);
  if (parts.length === 0) throw new Error(`enemy ${d.id}: no art loaded (all kinds failed)`);

  // ONE BACKEND PER CREATURE. A creature is either 2D art or 3D — an owner
  // decision, and the reason there is no runtime A/B toggle any more. If a def
  // somehow names both, the 3D backend (rig) wins and the sprite is freed
  // immediately rather than sitting in VRAM for a renderer that will never ask
  // for it.
  const threePart = parts.find((p) => p.rig);
  if (threePart && parts.some((p) => p.sprite)) {
    warnOnce(`${d.id}: art names both a rig and a sprite — a creature is one or the other; keeping the rig`);
    const doomed = parts.filter((p) => p.sprite && !p.rig);
    for (const p of doomed) {
      p.sprite?.atlas?.dispose?.();
      p.dispose?.();
    }
    parts = parts.filter((p) => !doomed.includes(p));
  }

  const sprite = threePart ? null : parts.find((p) => p.sprite)?.sprite ?? null;
  const rig = threePart?.rig ?? null;
  const s = d.stats;
  const radius = num(s.radius) > 0 ? num(s.radius) : 0.45;

  const type = defineEnemyType({
    id: d.id,
    name: d.name,
    radius,
    speed: s.speed,
    hp: s.hp,
    damage: s.damage,
    attackRange: s.attackRange,
    attackCooldown: s.attackCooldown,
    aggroRange: s.aggroRange,
    // Ranged cadence, read only under team targeting. `defineEnemyType`
    // validates it — a def with
    // a half-written `fire` block stays melee rather than becoming inert.
    fire: s.fire,
    worldHeight,
    blobR: s.blobR ?? radius * 1.15,
    tint: s.tint,
    rig,
    sprite,
    def: d,
  });

  const baseDispose = type.dispose.bind(type);
  type.dispose = () => {
    baseDispose();
    for (const p of parts) p.dispose?.();
  };
  return type;
}

/**
 * Loads a roster. Individual failures are logged and SKIPPED — one bad def must
 * not take a level down with it.
 *
 * @param {EnemyDef[]} defs
 * @param {Parameters<typeof loadEnemyType>[1]} [opts]
 * @returns {Promise<EnemyType[]>} In input order, minus the failures.
 */
export async function loadEnemyTypes(defs, opts = {}) {
  const out = await Promise.all(
    (defs ?? []).map((d) =>
      loadEnemyType(d, opts).catch((err) => {
        console.warn(`[enemyArt] skipping ${d?.id ?? '?'}: ${err?.message ?? err}`);
        return null;
      }),
    ),
  );
  return /** @type {EnemyType[]} */ (out.filter(Boolean));
}

// ─────────────────────────────────────────────────────────────────────────────
// Kind: sprite — a flat authored PNG
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A 2D enemy looks the same from every angle, which is CORRECT for this art
 * style — so there is exactly one atlas row and `rowFor` is a constant. Multiple
 * `frames` lay out left-to-right and are driven by the manager's walk phase, the
 * same phase that drives the tilt and bob.
 *
 * @param {{url: string, worldHeight: number, frames?: number, hover?: number}} art
 * @param {any} ctx
 * @returns {Promise<EnemyArtResult>}
 */
async function loadSpriteArt(art, ctx) {
  // The url requirement lives HERE, not in `normalizeEnemyDef`: whether a kind
  // needs a file is per-kind knowledge (the `rig` kind names a generator).
  if (typeof art.url !== 'string' || !art.url) {
    throw new Error(`enemy def ${ctx.def.id}: art.sprite.url is required`);
  }
  const texture = await new THREE.TextureLoader().loadAsync(art.url);
  configureSpriteTexture(texture, ctx.anisotropy);
  texture.name = `enemy:${ctx.def.id}:sprite`;

  const frames = Math.max(1, Math.floor(num(art.frames)) || 1);
  if (frames > 1) {
    // Mipmaps average ACROSS frame borders on a strip, so the walk frame bleeds
    // into the idle frame at distance. Single-frame art keeps them (the shimmer
    // fix that `configureSpriteTexture` exists for).
    texture.minFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
  }

  const w = texture.image?.width ?? 0;
  const h = texture.image?.height ?? 0;
  const worldH = art.worldHeight;
  const hover = Math.max(0, num(art.hover));

  return {
    sprite: {
      atlas: texture,
      rows: 1,
      cols: frames,
      frameW: frames > 0 ? w / frames : w,
      frameH: h,
      worldH,
      // Negative = the quad floats clear of the ground. See the header.
      anchorY: hover > 0 && worldH > 0 ? -hover / worldH : 0,
      rowFor: () => 0,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Kind: rig — a procedural creature built in code (engine/actors/creatures.js)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The 3D kind. No file, no fetch: the def names a registered creature
 * GENERATOR (a module in `src/game/creatures/`) plus its params, and
 * `buildRig` assembles, scales and rest-pose-bakes the parts synchronously.
 * External model loading (`.glb`) was deliberately dropped — owner decision,
 * 2026-08-03; see HANDOFF-RIG.md.
 *
 * @param {{generator: string, params?: Object, worldHeight: number}} art
 * @param {any} ctx
 * @returns {Promise<EnemyArtResult>}
 */
async function loadRigArt(art, ctx) {
  if (typeof art.generator !== 'string' || !art.generator) {
    throw new Error(`enemy def ${ctx.def.id}: art.rig.generator is required`);
  }
  const rig = buildRig(art.generator, {
    params: art.params ?? {},
    worldHeight: art.worldHeight,
  });
  return {
    rig,
    dispose: () => rig.dispose(),
  };
}

/** @param {any} v @returns {number} `0` for anything non-finite. */
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

registerEnemyArtKind('sprite', loadSpriteArt);
registerEnemyArtKind('rig', loadRigArt);
